// HomePage.js
import React, { useState, useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import ServiceCard from "../components/sections/ServiceCard";
import FAQSection from "../components/sections/FAQSection";
import PatientReviews from "../components/sections/PatientReviews";
import '../styles/HomePage.css';
// Add this with your other imports
import bgImage from '../assets/animations/bg_image.jpeg';
// Import video
import heroVideoLow from '../assets/animations/home_low.mp4';
import heroVideoHigh from '../assets/animations/home_high.mp4';

// Import images
import support24 from '../assets/animations/24_card.jpg';
import compassionate from '../assets/animations/compasionate.jpg';
import profNurse from '../assets/animations/prof_nurse.jpg';
import contactSupport from '../assets/animations/contact_support.jpg';

// Import JSON animations
import catheterJson from '../assets/animations/catheter.json';
import basicMobilityJson from '../assets/animations/basic_mobility.json';
import demenEnvJson from '../assets/animations/demen-env.json';
import physioFlexJson from '../assets/animations/Physio_flex.json';
import opMonitorJson from '../assets/animations/op_monitor.json';

// Add this line with the other imports at the top
import woundChronicImage from '../assets/animations/wound_chronic.jpg';
// In HomePage.js


const HomePage = ({ services }) => {
    const [, setVideoLoaded] = useState(false);
    const [videoInView, setVideoInView] = useState(false);
    const [useHighQuality, setUseHighQuality] = useState(false);
    const videoRef = useRef(null);
    
   useEffect(() => {
  const currentVideo = videoRef.current; // ✅ copy ref to a stable variable

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setVideoInView(true);
        observer.disconnect();
      }
    },
    { threshold: 0.1 }
  );

  if (currentVideo) {
    observer.observe(currentVideo);
  }

  return () => {
    if (currentVideo) {
      observer.unobserve(currentVideo);
    }
  };
}, []); // ✅ no dependencies needed

    
    useEffect(() => {
        if (videoInView) {
            // Check network connection
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            
            if (connection) {
                // If we have a good connection, use high quality
                if (connection.effectiveType === '4g' && !connection.saveData) {
                    setUseHighQuality(true);
                }
            } else {
                // If we can't detect the connection, use high quality by default
                setUseHighQuality(true);
            }
            
            const timer = setTimeout(() => {
                setVideoLoaded(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [videoInView]);
    

    // Features data
    const features = [
        {
            title: "24/7 Availability",
            description: "Round-the-clock care when you need it most",
            image: support24,
            alt: "24/7 Availability"
        },
        {
            title: "Compassionate Care",
            description: "Kind, respectful treatment for all patients",
            image: compassionate,
            alt: "Compassionate Care"
        },
        {
            title: "Professional Staff",
            description: "Experienced and certified healthcare providers",
            image: profNurse,
            alt: "Professional Staff"
        }
    ];
    const homeFaqs = [
  {
    question: "What services do you provide?",
    answer:
      "We provide a wide range of home care services including elderly care, post-surgery recovery, companionship, personal care, and specialized support tailored to each individual’s needs.",
  },
  {
    question: "Are your caregivers trained and certified?",
    answer:
      "Yes, all our caregivers are experienced, thoroughly trained, and certified to provide compassionate and professional care.",
  },
  {
    question: "Do you offer 24/7 care?",
    answer:
      "Absolutely. We provide round-the-clock support to ensure safety and comfort at any hour of the day or night.",
  },
  {
    question: "Can I customize the care plan?",
    answer:
      "Yes. Every individual is unique, so we design personalized care plans in consultation with families to match specific needs.",
  },
  {
    question: "How do I get started?",
    answer:
      "Simply reach out to us through our contact page or give us a call. Our team will guide you through the process and help set up a care plan.",
  },


];
    
    // Define services with animations using the same structure as other pages
    // Update the homeServices array to include routes
const homeServices = [
    {
        title: "Basic Supportive Care",
        description: "Assistance with daily activities and basic support for independent living.",
        features: [
            "✔ Personal care assistance",
            "✔ Meal preparation",
            "✔ Light housekeeping"
        ],
        animation: basicMobilityJson,
        animationType: 'lottie',
        route: "/basic-supportive-care"  // CHANGED from "/services/basic-supportive-care"
    },
    {
        title: "Advanced Nursing Care",
        description: "Specialized medical care including catheterization and IV therapy.",
        features: [
            "✔ Catheterization",
            "✔ Ryles tube management",
            "✔ IV injections"
        ],
        animation: catheterJson,
        animationType: 'lottie',
        route: "/advanced-nursing-care"  // CHANGED from "/services/advanced-nursing-care"
    },
    {
        title: "Dementia Care",
        description: "Specialized care for patients with dementia to ensure safety and comfort.",
        features: [
            "✔ Cognitive stimulation",
            "✔ Safety supervision",
            "✔ Behavior management"
        ],
        animation: demenEnvJson,
        animationType: 'lottie',
        route: "/dementia-care"  // CHANGED from "/services/dementia-care"
    },
    {
        title: "Physiotherapy",
        description: "Rehabilitation services to improve mobility and physical function.",
        features: [
            "✔ Mobility training",
            "✔ Pain management",
            "✔ Strength exercises"
        ],
        animation: physioFlexJson,
        animationType: 'lottie',
        route: "/physiotherapy"  // CHANGED from "/services/physiotherapy"
    },
    {
        title: "Pre & Post-Operative Care",
        description: "Comprehensive care before and after surgical procedures.",
        features: [
            "✔ Pre-op preparation",
            "✔ Wound care",
            "✔ Recovery monitoring"
        ],
        animation: opMonitorJson,
        animationType: 'lottie',
        route: "/pre-post-op-care"  // CHANGED from "/services/pre-post-operative-care"
    },
    {
        title: "Wound Dressing",
        description: "Professional care for wounds to promote healing and prevent infection.",
        features: [
            "✔ Infection control",
            "✔ Wound cleaning",
            "✔ Dressing changes"
        ],
        animation: woundChronicImage,
        animationType: 'image',
        route: "/wound-dressing"  // CHANGED from "/services/wound-dressing"
    }
];

    
        // ... rest of the code
    
    return (
        <div className="page-layout">
            {/* Hero Section */}
            <div className="hero-section">
                {/* Video Background */}
                <video 
                    ref={videoRef}
                    className="hero-video" 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    preload="metadata"
                    poster={bgImage}
                >
                    {videoInView && (
                        <>
                            {useHighQuality ? (
                                <source src={heroVideoHigh} type="video/mp4" />
                            ) : (
                                <source src={heroVideoLow} type="video/mp4" />
                            )}
                        </>
                    )}
                    Your browser does not support the video tag.
                </video>
                
                {/* Overlay Content */}
                <div className="hero-overlay">
                    <motion.h1 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="hero-title"
                    >
                        Timeless Home Care
                    </motion.h1>
                    
                    <motion.p 
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="hero-subtitle"
                    >
                        Where compassion meets comfort, ensuring dignity and joy at every stage of life.
                    </motion.p>
                    
                    <motion.a 
                        href="/contact" 
                        className="hero-cta"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Contact Us
                    </motion.a>
                </div>
            </div>
            
            {/* Services Section */}
            <section className="services-section">
                <div className="section-header">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="services-title"
                    >
                        Our Services
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="services-subtitle"
                    >
                        Compassionate, personalized healthcare designed to bring peace and comfort 
                        to you and your loved ones.
                    </motion.p>
                </div>
                
                <div className="services-grid">
                    {homeServices.map((service, index) => (
                        <motion.div 
                            key={index}
                            className="service-item"
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ 
                                duration: 0.6,
                                delay: index * 0.2
                            }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                        >
                        
<ServiceCard 
    title={service.title}
    description={service.description}
    features={service.features}
    animation={service.animation}
    animationType={service.animationType}
    route={service.route}  // Add this prop
/>
                        </motion.div>
                    ))}
                </div>
            </section>
            
            {/* Features Section */}
            <section className="features-section">
                <div className="section-header">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="services-title"
                    >
                        Our Commitment to Excellence
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="services-subtitle"
                    >
                        We provide exceptional care with compassion and professionalism.
                    </motion.p>
                </div>
                
                <div className="features-container">
                    {features.map((feature, index) => (
                        <motion.div 
                            key={index}
                            className={`feature-item ${index % 2 === 0 ? 'feature-left' : 'feature-right'}`}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ 
                                duration: 0.8,
                                delay: index * 0.2
                            }}
                            viewport={{ once: true }}
                        >
                            <div className="feature-media">
                                <div className="feature-image" style={{ backgroundImage: `url(${feature.image})` }}></div>
                            </div>
                            <div className="feature-content">
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>
            
            {/* Call to Action */}
            <section className="cta-section">
                <div className="cta-bg" style={{ backgroundImage: `url(${contactSupport})` }}></div>
                <div className="cta-overlay"></div>
                <motion.div 
                    className="cta-box"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7 }}
                    whileHover={{ y: -10 }}
                >
                    <motion.h2
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Need Support for a Loved One?
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        Our team is here 24/7 to provide professional, compassionate, 
                        and personalized care services.
                    </motion.p>
                    <motion.a 
                        href="/contact" 
                        className="cta-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        Get in Touch
                    </motion.a>
                </motion.div>
            </section>
            
            <FAQSection 
        faqs={homeFaqs} 
        subtitle="Find answers to some of the most common questions about our home care services."
      />
            <PatientReviews />
        </div>
    );
};

export default HomePage;